import React, { useState, useEffect, useRef } from "react";
import { Icon, Modal, Header } from "semantic-ui-react";
import { toast } from "react-toastify";
import { LegalRequirement } from "../../../../api";
import { useAuth } from "../../../../hooks";
import { useLanguage } from "../../../../contexts";
import { EvidenceTextButton } from "../../../Shared";

const lrController = new LegalRequirement();

const FREQ_CONFIG = {
  "diario":        { count: 365, addMonths: 0, addDays: 1 },
  "semanal":       { count: 52,  addMonths: 0, addDays: 7 },
  "quincenal":     { count: 24,  addMonths: 0, addDays: 15 },
  "mensual":       { count: 12,  addMonths: 1, addDays: 0 },
  "bimestral":     { count: 6,   addMonths: 2, addDays: 0 },
  "trimestral":    { count: 4,   addMonths: 3, addDays: 0 },
  "cuatrimestral": { count: 3,   addMonths: 4, addDays: 0 },
  "semestral":     { count: 2,   addMonths: 6, addDays: 0 },
  "anual":         { count: 1,   addMonths: 12, addDays: 0 },
};


function addInterval(date, config) {
  const d = new Date(date);
  if (config.addMonths) d.setMonth(d.getMonth() + config.addMonths);
  else d.setDate(d.getDate() + config.addDays);
  return d;
}

function toDateStr(date) {
  if (!date) return "";
  const d = new Date(date);
  return isNaN(d) ? "" : d.toISOString().split("T")[0];
}

function formatDateTime(date) {
  if (!date) return "";
  return new Date(date).toLocaleString("es-AR", { dateStyle: "short", timeStyle: "short" });
}

export function LegalRequirementDates({ item, close, onReload, onLocalSave, customSave, fileApi, evidenceModule = "legalRequirement", showFooter = false, hideTitle = false, capOccurrencesToYear = false }) {
  const { accessToken, user } = useAuth();
  const { translations } = useLanguage();
  const t = (key) => translations[key] || key;
  const userName = user ? `${user.firstname || ""} ${user.lastname || ""}`.trim() : "Usuario";
  const freqName = item.frequency?.name?.toLowerCase().trim() || "";
  const freqConfig = FREQ_CONFIG[freqName];

  const [saving, setSaving] = useState(false);
  const [startDate, setStartDate] = useState(() => {
    if (item.expiration) return toDateStr(item.expiration);
    return "";
  });

  const [occurrences, setOccurrences] = useState(() => {
    if (item.occurrences?.length) {
      return item.occurrences.map((o) => ({
        date:      toDateStr(o.date),
        completed: o.completed || false,
        comments:  o.comments  || [],
        files:     o.files     || [],
      }));
    }
    return [];
  });

  // Sync occurrences/progress to parent on every change (for row-level save)
  useEffect(() => {
    if (onLocalSave) onLocalSave(occurrences, progress);
  }, [occurrences]);

  // Panel open state per occurrence
  const [openPanel, setOpenPanel] = useState(null); // null | { idx, type: "comments"|"files" }
  const [commentText, setCommentText] = useState("");

  // File upload per occurrence
  const fileInputRef   = useRef(null);
  const [occUploadIdx,   setOccUploadIdx]   = useState(null);
  const [uploadingOcc,   setUploadingOcc]   = useState(false);
  const [deletingOccFile, setDeletingOccFile] = useState(null); // { occIdx, key }
  const [occPreviewUrl,  setOccPreviewUrl]  = useState(null);
  const [occPreviewName, setOccPreviewName] = useState("");

  const isImage = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name || "");

  const triggerOccUpload = (idx) => {
    setOccUploadIdx(idx);
    fileInputRef.current.value = "";
    fileInputRef.current.click();
  };

  const handleOccFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setUploadingOcc(true);
    try {
      const added = [];
      for (const f of files) {
        const formData = new FormData();
        formData.append("file", f);
        const result = await (fileApi || lrController).uploadOccurrenceFile(accessToken, item._id, formData);
        added.push(result.file);
      }
      setOccurrences((prev) =>
        prev.map((o, idx) =>
          idx === occUploadIdx ? { ...o, files: [...(o.files || []), ...added] } : o
        )
      );
    } catch { toast.error(t("lrd_error_upload")); }
    finally { setUploadingOcc(false); }
  };

  const handleOccFileDelete = async (occIdx, fileKey) => {
    setDeletingOccFile({ occIdx, key: fileKey });
    try {
      await (fileApi || lrController).deleteFile(accessToken, item._id, fileKey);
    } catch { /* si falla en disco continuar igual */ }
    setOccurrences((prev) =>
      prev.map((o, idx) =>
        idx === occIdx ? { ...o, files: (o.files || []).filter((f) => f.key !== fileKey) } : o
      )
    );
    setDeletingOccFile(null);
  };

  const handleOccFileView = async (fileKey, fileName) => {
    try {
      const blobUrl = await (fileApi || lrController).getFile(accessToken, fileKey);
      if (!blobUrl) { toast.error(t("lrd_error_view")); return; }
      if (isImage(fileName)) {
        setOccPreviewUrl(blobUrl);
        setOccPreviewName(fileName);
      } else {
        window.open(blobUrl, "_blank");
      }
    } catch { toast.error(t("lrd_error_view")); }
  };

  const closeOccPreview = () => {
    if (occPreviewUrl) URL.revokeObjectURL(occPreviewUrl);
    setOccPreviewUrl(null);
    setOccPreviewName("");
  };

  const togglePanel = (idx, type) => {
    setOpenPanel((prev) =>
      prev?.idx === idx && prev?.type === type ? null : { idx, type }
    );
    setCommentText("");
  };

  const toggle = (i) =>
    setOccurrences((prev) =>
      prev.map((o, idx) => (idx === i ? { ...o, completed: !o.completed } : o))
    );

  const updateDate = (i, val) =>
    setOccurrences((prev) =>
      prev.map((o, idx) => (idx === i ? { ...o, date: val } : o))
    );

  const generate = () => {
    if (!startDate || !freqConfig) return;
    const list = [];
    let current = new Date(startDate + "T12:00:00");
    if (capOccurrencesToYear) {
      // Plan Anual: las fechas nunca cruzan al año siguiente — se generan
      // desde la fecha elegida hasta el 31/12 de ese mismo año.
      const yearEnd = new Date(current.getFullYear(), 11, 31, 12, 0, 0);
      let guard = 0;
      while (current <= yearEnd && guard < 366) {
        list.push({ date: toDateStr(current), completed: false, comments: [], files: [] });
        current = addInterval(current, freqConfig);
        guard++;
      }
    } else {
      for (let i = 0; i < freqConfig.count; i++) {
        list.push({ date: toDateStr(current), completed: false, comments: [], files: [] });
        current = addInterval(current, freqConfig);
      }
    }
    setOccurrences(list);
    setOpenPanel(null);
  };

  const addComment = (i) => {
    if (!commentText.trim()) return;
    setOccurrences((prev) =>
      prev.map((o, idx) =>
        idx === i
          ? { ...o, comments: [...o.comments, { text: commentText.trim(), userName, createdAt: new Date().toISOString() }] }
          : o
      )
    );
    setCommentText("");
  };

  const deleteComment = (occIdx, cmtIdx) =>
    setOccurrences((prev) =>
      prev.map((o, idx) =>
        idx === occIdx
          ? { ...o, comments: o.comments.filter((_, ci) => ci !== cmtIdx) }
          : o
      )
    );

  const completed = occurrences.filter((o) => o.completed).length;
  const total = occurrences.length;
  const progress = total ? Math.round((completed / total) * 100) : 0;
  const today = new Date().toISOString().split("T")[0];

  const handleSave = async () => {
    if (onLocalSave) {
      onLocalSave(occurrences, progress);
      close();
      return;
    }
    setSaving(true);
    try {
      if (customSave) {
        await customSave(occurrences, progress);
      } else {
        await lrController.updateLegalRequirement(accessToken, item._id, { occurrences, progress });
        toast.success(t("lrd_dates_saved"));
        onReload();
        close();
      }
    } catch {
      toast.error(t("ap_error_save"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ padding: "0.75rem" }}>
      {!hideTitle && (
        <h3 style={{ fontWeight: 700, fontSize: "1.25rem", color: "#1A2E4A", margin: "0 0 16px 0" }}>
          {t("register_advance")}
        </h3>
      )}

      {/* Fecha inicio — solo si no hay ocurrencias generadas */}
      {occurrences.length === 0 && freqConfig && (
        <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", marginBottom: "1rem" }}>
          <label style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{t("lrd_start_date")}:</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            style={{ padding: "4px 8px", border: "1px solid #ddd", borderRadius: 4 }}
          />
          <button
            type="button"
            onClick={generate}
            disabled={!startDate}
            style={{
              border: "none", borderRadius: "20px", padding: "7px 18px",
              fontSize: "13px", fontWeight: 600, fontFamily: "'Archivo', sans-serif",
              cursor: !startDate ? "not-allowed" : "pointer",
              background: "#4A7FA5", color: "#fff", opacity: !startDate ? 0.55 : 1,
            }}
          >
            {t("lrd_generate")}
          </button>
        </div>
      )}

      {/* Date grid */}
      {occurrences.length > 0 ? (
        <>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "0.5rem",
              marginBottom: "1rem",
              maxHeight: "420px",
              overflowY: "auto",
            }}
          >
            {occurrences.map((o, i) => {
              const expired = !o.completed && o.date && o.date < today;
              const isCommentsOpen = openPanel?.idx === i && openPanel?.type === "comments";
              const isFilesOpen    = openPanel?.idx === i && openPanel?.type === "files";

              const cardBg     = o.completed ? "#EEF3E0" : expired ? "#fde8e8" : "#fff";
              const cardBorder = o.completed ? "#C4D693" : expired ? "#f5c6c6" : "#e8e8e8";

              return (
                <div
                  key={i}
                  style={{
                    border: `1px solid ${cardBorder}`,
                    borderRadius: 10,
                    background: cardBg,
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.06)",
                  }}
                >
                  {/* Main row */}
                  <div style={{ display: "flex", alignItems: "center", padding: "0.4rem 0.6rem", gap: "0.5rem" }}>
                    {/* Checkbox styled */}
                    <label style={{ display: "flex", alignItems: "center", cursor: "pointer", flexShrink: 0 }}>
                      <input
                        type="checkbox"
                        checked={o.completed}
                        onChange={() => toggle(i)}
                        style={{ width: 16, height: 16, cursor: "pointer", accentColor: "#C4D693" }}
                      />
                    </label>

                    {/* Date */}
                    <input
                      type="date"
                      value={o.date}
                      onChange={(e) => updateDate(i, e.target.value)}
                      style={{
                        flex: 1, border: "none", background: "transparent",
                        fontSize: "0.88em", fontWeight: 500, color: "#333", minWidth: 0,
                      }}
                    />

                    {/* Vencida */}
                    {expired && (
                      <span style={{ fontSize: "0.78em", color: "#e03131", fontWeight: 600, whiteSpace: "nowrap" }}>
                        {t("lrd_expired")}
                      </span>
                    )}

                    {/* Action buttons */}
                    <div style={{ display: "flex", gap: "0.25rem", flexShrink: 0 }}>
                      <button
                        onClick={() => togglePanel(i, "comments")}
                        style={{
                          width: 28, height: 28, borderRadius: "50%", border: "none", cursor: "pointer",
                          background: "#CAE2F6",
                          color: "#0E2A47", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, position: "relative",
                        }}
                        title={t("lrd_comments")}
                      >
                        <Icon name="comment outline" style={{ margin: 0 }} />
                        {o.comments?.length > 0 && (
                          <span style={{
                            position: "absolute", top: -4, right: -4,
                            background: "#e03131", color: "#fff", borderRadius: "50%",
                            width: 14, height: 14, fontSize: "0.6em",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {o.comments.length}
                          </span>
                        )}
                      </button>
                      <button
                        onClick={() => togglePanel(i, "files")}
                        style={{
                          width: 28, height: 28, borderRadius: "50%", border: "none", cursor: "pointer",
                          background: "#648EBC",
                          color: "#fff", display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 13, position: "relative",
                        }}
                        title={t("lrd_files")}
                      >
                        <Icon name="attach" style={{ margin: 0 }} />
                        {o.files?.length > 0 && (
                          <span style={{
                            position: "absolute", top: -4, right: -4,
                            background: "#e03131", color: "#fff", borderRadius: "50%",
                            width: 14, height: 14, fontSize: "0.6em",
                            display: "flex", alignItems: "center", justifyContent: "center",
                          }}>
                            {o.files.length}
                          </span>
                        )}
                      </button>
                    </div>
                  </div>

                  {/* Comments panel */}
                  {isCommentsOpen && (
                    <div style={{ borderTop: "1px solid #e0e0e0", padding: "0.4rem 0.5rem", background: "#fff" }}>
                      {o.comments.length === 0 ? (
                        <p style={{ color: "#999", fontSize: "0.8em", margin: "0 0 0.4rem" }}>{t("lrd_no_comments")}</p>
                      ) : (
                        <div style={{ maxHeight: 120, overflowY: "auto", marginBottom: "0.4rem" }}>
                          {o.comments.map((c, ci) => (
                            <div
                              key={ci}
                              style={{
                                display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                                padding: "0.2rem 0", borderBottom: "1px solid #f0f0f0", gap: "0.3rem",
                              }}
                            >
                              <div style={{ flex: 1 }}>
                                <div style={{ fontSize: "0.72em", color: "#666", fontWeight: 600 }}>
                                  {c.userName || "Usuario"}
                                </div>
                                <span style={{ fontSize: "0.82em" }}>{c.text}</span>
                                <div style={{ fontSize: "0.7em", color: "#aaa" }}>{formatDateTime(c.createdAt)}</div>
                              </div>
                              <Icon
                                name="times" size="small" color="red"
                                style={{ cursor: "pointer", flexShrink: 0 }}
                                onClick={() => deleteComment(i, ci)}
                              />
                            </div>
                          ))}
                        </div>
                      )}
                      <div style={{ display: "flex", gap: "0.3rem" }}>
                        <input
                          type="text"
                          value={commentText}
                          onChange={(e) => setCommentText(e.target.value)}
                          onKeyDown={(e) => e.key === "Enter" && addComment(i)}
                          placeholder={t("lrd_add_comment_ph")}
                          style={{ flex: 1, fontSize: "0.82em", padding: "3px 6px", border: "1px solid #ddd", borderRadius: 3 }}
                        />
                        <button
                          type="button"
                          onClick={() => addComment(i)}
                          disabled={!commentText.trim()}
                          style={{
                            width: 28, height: 28, borderRadius: "50%", border: "none",
                            cursor: !commentText.trim() ? "not-allowed" : "pointer",
                            background: "#4A7FA5", color: "#fff",
                            display: "flex", alignItems: "center", justifyContent: "center",
                            opacity: !commentText.trim() ? 0.5 : 1, flexShrink: 0,
                          }}
                        >
                          <Icon name="send" style={{ margin: 0, fontSize: 11 }} />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Files panel */}
                  {isFilesOpen && (
                    <div style={{ borderTop: "1px solid #e0e0e0", padding: "0.4rem 0.5rem", background: "#fff" }}>
                      {(!o.files || o.files.length === 0) ? (
                        <p style={{ color: "#999", fontSize: "0.8em", margin: "0 0 0.4rem" }}>{t("lrd_no_files")}</p>
                      ) : (
                        <ul style={{ margin: 0, padding: "0 0 0.4rem 0", listStyle: "none" }}>
                          {o.files.map((f, fi) => (
                            <li key={fi} style={{ display: "flex", alignItems: "center", gap: "0.3rem", marginBottom: "0.3rem" }}>
                              <Icon name={isImage(f.name) ? "image outline" : "file outline"} style={{ color: "#555" }} />
                              <span style={{ flex: 1, fontSize: "0.8em", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</span>
                              <button onClick={() => handleOccFileView(f.key, f.name)} title={t("lrd_view")}
                                style={{ width: 22, height: 22, borderRadius: "50%", border: "none", cursor: "pointer", background: "#648EBC", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>
                                <Icon name="eye" style={{ margin: 0 }} />
                              </button>
                              <EvidenceTextButton module={evidenceModule} fileKey={f.key} fileName={f.name} style={{ width: 22, height: 22 }} />
                              <button
                                onClick={() => handleOccFileDelete(i, f.key)} title={t("delete")}
                                disabled={deletingOccFile?.occIdx === i && deletingOccFile?.key === f.key}
                                style={{ width: 22, height: 22, borderRadius: "50%", border: "none", cursor: "pointer", background: "#0E376E", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>
                                <Icon name="trash" style={{ margin: 0 }} />
                              </button>
                            </li>
                          ))}
                        </ul>
                      )}
                      <button
                        onClick={() => triggerOccUpload(i)}
                        disabled={uploadingOcc}
                        style={{ fontSize: "0.78em", padding: "3px 10px", borderRadius: 12, border: "1px solid #648EBC", background: uploadingOcc && occUploadIdx === i ? "#eee" : "#fff", color: "#648EBC", cursor: "pointer" }}
                      >
                        <Icon name={uploadingOcc && occUploadIdx === i ? "spinner" : "plus"} loading={uploadingOcc && occUploadIdx === i} style={{ margin: "0 4px 0 0" }} />
                        {t("lrd_attach")}
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Progress bar */}
          {/* <div
            style={{
              display: "flex", alignItems: "center", gap: "0.75rem",
              padding: "0.6rem 0.75rem", background: "#f4f6fb", borderRadius: 6, marginBottom: "1rem",
            }}
          >
            <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>Avance:</span>
            <div style={{ flex: 1, background: "#ddd", borderRadius: 4, height: 10 }}>
              <div
                style={{
                  width: `${progress}%`,
                  background: progress === 100 ? "#21ba45" : "#2185d0",
                  height: "100%", borderRadius: 4, transition: "width 0.3s",
                }}
              />
            </div>
            <strong style={{ minWidth: 38 }}>{progress}%</strong>
            <span style={{ color: "#666", fontSize: "0.85em", whiteSpace: "nowrap" }}>
              ({completed}/{total})
            </span>
          </div> */}
        </>
      ) : (
        <div style={{ textAlign: "center", color: "#999", padding: "2rem" }}>
          {freqConfig
            ? t("lrd_enter_start_date")
            : t("lrd_no_freq_assigned")}
        </div>
      )}

      {/* Footer actions */}
      {showFooter && (
        <div style={{ display: "flex", justifyContent: "center", gap: "14px", marginTop: "2.5rem" }}>
          <button
            type="button"
            disabled={saving || total === 0}
            onClick={handleSave}
            style={{
              border: "none", borderRadius: "20px", padding: "10px 30px",
              fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif",
              cursor: saving || total === 0 ? "not-allowed" : "pointer",
              background: "#4A7FA5", color: "#fff",
              opacity: saving || total === 0 ? 0.55 : 1,
            }}
          >
            {saving ? "…" : t("save")}
          </button>
          <button
            type="button"
            onClick={close}
            style={{
              border: "none", borderRadius: "20px", padding: "10px 30px",
              fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif",
              cursor: "pointer", background: "#9BAFC4", color: "#fff",
            }}
          >
            {t("cancel")}
          </button>
        </div>
      )}

      {/* Input oculto para subir archivos de ocurrencias */}
      <input type="file" multiple ref={fileInputRef} style={{ display: "none" }} onChange={handleOccFileChange} />

      {/* Modal preview imagen */}
      <Modal open={!!occPreviewUrl} onClose={closeOccPreview} size="large">
        <Header icon="image" content={occPreviewName} />
        <Modal.Content style={{ textAlign: "center", background: "#1a1a1a" }}>
          {occPreviewUrl && (
            <img src={occPreviewUrl} alt={occPreviewName}
              style={{ maxWidth: "100%", maxHeight: "75vh", objectFit: "contain" }} />
          )}
        </Modal.Content>
        <Modal.Actions>
          <button
            type="button"
            onClick={closeOccPreview}
            style={{
              border: "none", borderRadius: "20px", padding: "8px 22px",
              fontSize: "14px", fontWeight: 600, fontFamily: "'Archivo', sans-serif",
              cursor: "pointer", background: "#9BAFC4", color: "#fff",
            }}
          >
            <Icon name="close" />{t("close")}
          </button>
        </Modal.Actions>
      </Modal>
    </div>
  );
}
